import React, { useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@material-ui/core/Box'
import cn from 'classnames'
import { getInitializedValues, getCFDScreen, getAndroidConfig, getCartTotalsUuid, getTempCFDScreen } from '../Selectors'
import { VNIdle } from './VNIdle'
import { VNCart } from './VNCart'
import { VNPayment } from './VNPayment'
import { VNProcessing } from './VNProcessing'
import { VNPayWithApp } from './VNPayWithApp'
import { VNStatusScreen } from './VNStatusScreen'
import { VNBalanceDue } from './VNBalanceDue'
import { VNReceipt } from './VNReceipt'
import { Fragment } from 'react'
import { CFD_SCREEN } from '../Reducer'
import { ReactComponent as CardDeclined } from '../../assets/CFDCardDeclined.svg'
import { ReactComponent as CFDShowReceipt } from '../../assets/CFDShowReceipt.svg'
import { ReactComponent as CFDFinishTransactionOnPhone } from '../../assets/CFDFinishTransactionOnPhone.svg'
import { ReactComponent as CFDShowQRCode } from '../../assets/CFDShowQRCode.svg'
import { ReactComponent as CFDWaitForAttendant } from '../../assets/CFDWaitForAttendant.svg'
import ScanQRGif from '../../assets/gifs/scan-qr.gif'

import { Typography } from '@material-ui/core'
import { VNBalanceLookup } from './VNBalanceLookup'
import { isScreenAcceptable, sendCfdWebViewLoaded } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { VNCharity } from './VNCharity'
import createDataDogLog from '../../utils/datadog'
import { executeSaveConfig } from '../../utils/bridgeMethods'

export const VNCustomerFacingDisplay = () => {
  const useStyles = makeStyles((theme) => ({
    root: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#000000',
      overflowY: 'scroll',
    },
    s4Logo: {
      marginBottom: 20,
      marginRight: 20,
      height: 45,
    },
    imgBox: {
      position: 'absolute',
      bottom: 0,
      right: 0,
    },
    debugger: {
      backgroundColor: '#FFFF00',
      height: 1000,
      width: '100%',
      position: 'absolute',
      top: 600,
      right: 24,
      left: 24,
    },
    hidden: {
      display: 'none',
    },
  }))

  const classes = useStyles()
  const dispatch = useDispatch()

  // SELECTORS
  const isInitialized = useSelector((state) => getInitializedValues(state))
  const screen = useSelector((state) => getCFDScreen(state)) || CFD_SCREEN.IDLE // defaults to IDLE if undefined
  const tempScreen = useSelector(getTempCFDScreen)
  const androidConfig = useSelector((state) => getAndroidConfig(state))
  const cartTotalsUuid = useSelector(getCartTotalsUuid)
  // const debuggerText = useSelector(state => getDebugger(state))

  // LOCAL STATE

  // USE EFFECTS

  // ensures that we always go back to the idle screen
  // no matter where we are at, if we get another initialized call from the bridge
  // we need to go back to the idle screen.
  useEffect(() => {
    isScreenAcceptable(screen, cartTotalsUuid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isInitialized])

  useEffect(() => {
    sendCfdWebViewLoaded(true)
  }, [])

  useEffect(() => {
    createDataDogLog('info', `Saving device config on A3700 in VNCFD`, { config: androidConfig })
    executeSaveConfig(androidConfig)
  }, [androidConfig])

  const displayScreen = () => {
    // we use a temp screen so we don't lose the real screen in case we need to revert back to it
    if (tempScreen === CFD_SCREEN.SPLIT_PAY_WAITING_FOR_ATTENDANT) {
      return <VNStatusScreen topElement={<CFDWaitForAttendant />} title={'Waiting For Attendant'} type={screen} />
    }

    if (screen === CFD_SCREEN.CART) {
      return <VNCart />
    } else if (
      screen === CFD_SCREEN.CARD_PAYMENT ||
      screen === CFD_SCREEN.TRANSACTION_SUMMARY ||
      screen === CFD_SCREEN.RICH_CHECKOUT
    ) {
      return <VNPayment type={screen} />
    } else if (screen === CFD_SCREEN.RECEIPT) {
      return <VNReceipt />
    } else if (screen === CFD_SCREEN.PROCESSING) {
      return <VNProcessing />
    } else if (screen === CFD_SCREEN.CARD_DECLINED) {
      return <VNStatusScreen topElement={<CardDeclined />} type={screen} />
    } else if (screen === CFD_SCREEN.RICH_CHECKOUT_FINISH_TRANSACTION_ON_PHONE) {
      return (
        <VNStatusScreen
          topElement={<CFDFinishTransactionOnPhone />}
          title={'Please finish this transaction on your phone'}
          type={screen}
        />
      )
    } else if (screen === CFD_SCREEN.RICH_CHECKOUT_SHOW_RECEIPT) {
      return (
        <VNStatusScreen
          topElement={<CFDShowReceipt />}
          title={'Please show your mobile receipt to the attendant to confirm your order.'}
          type={screen}
        />
      )
    } else if (screen === CFD_SCREEN.RICH_CHECKOUT_QR_CODE) {
      return <VNPayWithApp />
    } else if (screen === CFD_SCREEN.SPLIT_PAY_WAITING_FOR_ATTENDANT) {
      return <VNStatusScreen topElement={<CFDWaitForAttendant />} title={'Waiting For Attendant'} type={screen} />
    } else if (screen === CFD_SCREEN.PRESENT_QR_PAY_CODE) {
      return (
        <VNStatusScreen
          topElement={<CFDShowQRCode />}
          title={'Please present your QR Pay code to the attendant.'}
          type={screen}
        />
      )
    } else if (screen === CFD_SCREEN.TICKET_PAYMENT) {
      return (
        <VNStatusScreen
          topElement={<img src={ScanQRGif} style={{ width: 200, height: 200 }} alt={'Scan Ticket'} />}
          title={'Please present your tickets to the attendant.'}
          type={screen}
        />
      )
    } else if (screen === CFD_SCREEN.GIFT_CARD_PAYMENT) {
      return (
        <VNStatusScreen
          topElement={<img src={ScanQRGif} style={{ width: 200, height: 200 }} alt={'Scan Gift Card'} />}
          title={'Please present your gift card to the attendant.'}
          type={screen}
        />
      )
    } else if (screen === CFD_SCREEN.BALANCE_AVAILABLE) {
      return <VNBalanceLookup />
    } else if (screen === CFD_SCREEN.BALANCE_DUE) {
      return <VNBalanceDue />
    } else if (screen === CFD_SCREEN.TICKET_PAYMENT_BALANCE) {
      return (
        <VNStatusScreen
          topElement={<VNBalanceDue displayHorizontal={true} />}
          title={<img src={ScanQRGif} style={{ width: 200, height: 200 }} alt={'Scan Ticket'} />}
          subtitle={
            <Typography style={{ paddingTop: 24, width: 523, textAlign: 'center' }} variant="h3">
              Please present your ticket to the attendant.
            </Typography>
          }
          type={screen}
        />
      )
    } else if (screen === CFD_SCREEN.CHARITY) {
      return <VNCharity />
    }
  }

  const display = () => {
    if (!isInitialized.isEmpty() && screen) {
      return displayScreen()
    }
  }

  return (
    <Fragment>
      <Box className={classes.root}>{display()}</Box>
      <Box
        className={cn([
          classes.root,
          (tempScreen === CFD_SCREEN.SPLIT_PAY_WAITING_FOR_ATTENDANT || screen !== CFD_SCREEN.IDLE) && classes.hidden,
        ])}
      >
        <VNIdle
          title={isInitialized.get('title')}
          standImage={isInitialized?.get('image')}
          menuImage={isInitialized?.get('menuImage')}
          color={isInitialized.get('color')}
        />
      </Box>
      <Box className={classes.imgBox}>
        <img alt="Powered By Shift4 Payments" src="./imgs/powered_by_shift4.png" className={classes.s4Logo} />
      </Box>
      {/* <Box className={classes.debugger}>
        <div>DEBUGGER</div>
        {debuggerText}
      </Box> */}
    </Fragment>
  )
}
